/* Amares Homes — section behaviour
   - Scroll-scrubbed hero (pinned stage, cross-fading beds)
   - Reveal-on-scroll for custom sections
   - Deferred video play for the assembly section
   All of it degrades to something usable without JS or with reduced motion. */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------------------------------------------------------------- Hero -- */

  function Hero(root) {
    this.root = root;
    this.sticky = root.querySelector('[data-amx-sticky]');
    this.beds = Array.prototype.slice.call(root.querySelectorAll('[data-amx-bed]'));
    this.panels = Array.prototype.slice.call(root.querySelectorAll('[data-amx-tx]'));
    this.bgs = Array.prototype.slice.call(root.querySelectorAll('[data-amx-bg]'));
    this.tabs = Array.prototype.slice.call(root.querySelectorAll('[data-amx-tab]'));
    this.progress = root.querySelector('[data-amx-progress]');
    this.handoff = root.querySelector('[data-amx-handoff]');
    // Slide count comes from whichever layer is present. Cut-outs used to be
    // the only source, which meant a hero running on backgrounds alone never
    // started — now that hiding the cut-out is a supported setting, that would
    // have been a silent dead hero.
    var slideIds = {};
    [this.beds, this.bgs, this.panels].forEach(function (group) {
      group.forEach(function (el) {
        var i = el.getAttribute('data-amx-bed') || el.getAttribute('data-amx-bg') || el.getAttribute('data-amx-tx');
        if (i !== null) slideIds[i] = true;
      });
    });
    this.count = Object.keys(slideIds).length;

    this.active = -1;
    this.ticking = false;
    this.zone = 0.82;

    /* Slideshow mode swaps only what *drives* the position — arrows and an
       index instead of scroll offset. The cross-fade, damping and layout are
       identical, so both modes stay in step by construction. */
    this.slideshow = root.dataset.amxMode === 'slideshow';
    this.prevBtn = root.querySelector('[data-amx-prev]');
    this.nextBtn = root.querySelector('[data-amx-next]');
    this.autoMs = parseInt(root.dataset.amxAutoplay, 10) || 0;
    this.autoTimer = null;

    // Damping: the render position chases the scroll position instead of being
    // pinned to it, which is what stops fast flicks reading as a hard cut.
    // 1 = no damping, lower = heavier. Frame-rate corrected in tick().
    var smoothing = parseFloat(root.dataset.amxSmoothing);
    this.lerp = isNaN(smoothing) ? 0.18 : Math.max(0.02, Math.min(1, 1 - smoothing / 100));

    // Share of each slide's scroll spent changing over. The rest is the plateau
    // where one slide sits alone, which is what you read and click in.
    var fade = parseFloat(root.dataset.amxFade);
    this.fade = isNaN(fade) ? 0.3 : Math.max(0.08, Math.min(0.6, fade / 100));
    this.target = 0;
    this.rendered = 0;
    this.settled = true;
    this.lastTime = 0;

    if (!this.count) return;

    this.onScroll = this.onScroll.bind(this);
    this.request = this.request.bind(this);

    this.tabs.forEach(
      function (tab) {
        tab.addEventListener(
          'click',
          function () {
            this.goTo(parseInt(tab.getAttribute('data-amx-tab'), 10));
          }.bind(this)
        );
      }.bind(this)
    );

    if (this.slideshow) this.bindControls();

    this.setMode();
    reduceMotion.addEventListener('change', this.setMode.bind(this));
  }

  Hero.prototype.bindControls = function () {
    var self = this;

    if (this.prevBtn) this.prevBtn.addEventListener('click', function () { self.step(-1); });
    if (this.nextBtn) this.nextBtn.addEventListener('click', function () { self.step(1); });

    // Arrow keys once focus is anywhere inside the hero.
    this.root.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') { e.preventDefault(); self.step(-1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); self.step(1); }
    });

    // Autoplay should never move under someone reading or tabbing through.
    ['mouseenter', 'focusin'].forEach(function (evt) {
      self.root.addEventListener(evt, function () { self.paused = true; });
    });
    ['mouseleave', 'focusout'].forEach(function (evt) {
      self.root.addEventListener(evt, function () { self.paused = false; });
    });

    // Swipe. Only horizontal gestures count, or a vertical page scroll that
    // happens to start on the hero would flick the slide.
    var x0 = null, y0 = null;
    var stage = this.sticky || this.root;
    stage.addEventListener('touchstart', function (e) {
      x0 = e.changedTouches[0].clientX;
      y0 = e.changedTouches[0].clientY;
    }, { passive: true });

    stage.addEventListener('touchend', function (e) {
      if (x0 === null) return;
      var dx = e.changedTouches[0].clientX - x0;
      var dy = e.changedTouches[0].clientY - y0;
      if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.5) self.step(dx < 0 ? 1 : -1);
      x0 = y0 = null;
    }, { passive: true });
  };

  Hero.prototype.setMode = function () {
    if (this.slideshow) {
      /* A slideshow works under reduced motion too — it just changes instantly
         instead of tweening, which is the honest reading of the preference:
         no involuntary movement, but the control still does its job. */
      this.static = false;
      this.root.classList.remove('amx-hero--static');
      this.ease = reduceMotion.matches ? 1 : this.lerp;
      window.removeEventListener('scroll', this.request);
      this.render(this.rendered);
      this.setActive(this.normalIndex(this.rendered));
      this.restartAuto();
      return;
    }

    this.ease = this.lerp;
    this.static = reduceMotion.matches;
    this.root.classList.toggle('amx-hero--static', this.static);

    if (this.static) {
      window.removeEventListener('scroll', this.request);
      window.removeEventListener('resize', this.request);
      this.clearInline();
      this.select(0);
    } else {
      window.addEventListener('scroll', this.request, { passive: true });
      window.addEventListener('resize', this.request, { passive: true });
      this.onScroll();
    }
  };

  /* ---------------------------------------------------------- slideshow -- */

  Hero.prototype.normalIndex = function (v) {
    return ((Math.round(v) % this.count) + this.count) % this.count;
  };

  Hero.prototype.setTarget = function (v) {
    this.target = v;
    if (this.settled) {
      this.settled = false;
      this.lastTime = 0;
      var self = this;
      window.requestAnimationFrame(function (t) {
        self.tick(t);
      });
    }
  };

  /** Advance by n slides, wrapping in whichever direction is shorter. */
  Hero.prototype.step = function (n) {
    this.setTarget(this.target + n);
    this.restartAuto();
  };

  Hero.prototype.startAuto = function () {
    if (!this.slideshow || !this.autoMs || reduceMotion.matches) return;
    var self = this;
    this.autoTimer = window.setTimeout(function () {
      // Nothing should advance under a visitor who isn't looking at it.
      if (!document.hidden && !self.paused) self.setTarget(self.target + 1);
      self.startAuto();
    }, this.autoMs);
  };

  Hero.prototype.restartAuto = function () {
    window.clearTimeout(this.autoTimer);
    this.startAuto();
  };

  Hero.prototype.clearInline = function () {
    this.beds.concat(this.panels).forEach(function (el) {
      el.style.opacity = '';
      el.style.transform = '';
    });
  };

  /* Static mode: tabs act as a plain picker. */
  Hero.prototype.select = function (i) {
    this.beds.concat(this.panels).forEach(function (el) {
      var own = parseInt(el.getAttribute('data-amx-bed') || el.getAttribute('data-amx-tx'), 10);
      if (own === i) el.setAttribute('data-amx-active', '');
      else el.removeAttribute('data-amx-active');
    });
    this.setActive(i);
  };

  /** Scroll handler: move the target, make sure the damping loop is awake. */
  Hero.prototype.request = function () {
    if (this.static) return;
    this.target = this.scrollProgress();
    if (this.settled) {
      this.settled = false;
      this.lastTime = 0;
      var self = this;
      window.requestAnimationFrame(function (t) {
        self.tick(t);
      });
    }
  };

  Hero.prototype.scrollProgress = function () {
    var m = this.metrics();
    return Math.max(0, Math.min(1, (window.scrollY - m.top) / m.span));
  };

  Hero.prototype.tick = function (now) {
    if (this.static) {
      this.settled = true;
      return;
    }

    var dt = this.lastTime ? Math.min(now - this.lastTime, 100) : 16.67;
    this.lastTime = now;

    // Frame-rate corrected easing, so a 120Hz display doesn't animate twice as fast.
    var factor = 1 - Math.pow(1 - (this.ease || this.lerp), dt / 16.67);
    var delta = this.target - this.rendered;

    if (Math.abs(delta) < 0.00015) {
      this.rendered = this.target;
      // Looping walks the index past the ends; fold it back before the final
      // paint so the settled frame is drawn at the normalised index.
      if (this.slideshow) {
        var folded = ((this.target % this.count) + this.count) % this.count;
        this.target = folded;
        this.rendered = folded;
      }
      this.render(this.rendered);
      this.settled = true;
      return;
    }

    this.rendered += delta * factor;
    this.render(this.rendered);

    var self = this;
    window.requestAnimationFrame(function (t) {
      self.tick(t);
    });
  };

  Hero.prototype.metrics = function () {
    var top = 0;
    var node = this.root;
    while (node) {
      top += node.offsetTop;
      node = node.offsetParent;
    }
    return { top: top, span: Math.max(1, this.root.offsetHeight - window.innerHeight) };
  };

  /* Maps a bed's distance from the current scroll position onto opacity,
     vertical drift and scale. Mirrors the mockup's easing. */
  /* Below this a panel is decorative: still fading, but no longer a click
     target. Without it the outgoing slide sits on top of the incoming one and
     swallows taps on a button the visitor can barely see. */
  var INTERACTIVE_AT = 0.9;

  /**
   * Opacity/drift for one slide at fractional position t (0 = this slide is
   * centred, 1 = the next one is).
   *
   * hold is deliberately 1 - fade, which makes the exit finish exactly as the
   * next slide's entrance finishes. Any longer and the outgoing slide is still
   * on screen after the incoming one has fully arrived — visible as a lingering
   * ghost of the previous slide.
   */
  Hero.prototype.place = function (el, index, drift, bp, relFade, relScale, rel) {
    var fade = this.fade;
    var hold = 1 - fade;
    var t = bp - index;

    // Looping: judge each slide at its nearest virtual position, otherwise the
    // wrap from last to first animates backwards through everything between.
    if (this.slideshow && this.count > 1) {
      var half = this.count / 2;
      while (t > half) t -= this.count;
      while (t < -half) t += this.count;
    }
    var opacity;
    var y;
    var scale;

    if (t <= -fade || t >= 1) {
      opacity = 0;
      y = t < 0 ? drift : -drift;
      scale = 0.94;
    } else if (t < 0) {
      var enter = (t + fade) / fade;
      opacity = enter;
      y = drift * (1 - enter);
      scale = 0.94 + 0.06 * enter;
    } else if (t <= hold) {
      opacity = 1;
      y = 0;
      scale = 1;
    } else {
      var exit = (t - hold) / fade;
      opacity = 1 - exit;
      y = -drift * exit;
      scale = 1 - 0.06 * exit;
    }

    var shown = opacity * relFade;
    el.style.opacity = String(shown);
    el.style.transform = 'translateY(' + (y - rel * 40) + 'px) scale(' + scale * relScale + ')';
    el.style.pointerEvents = shown >= INTERACTIVE_AT ? 'auto' : 'none';
  };

  /** Jump straight to the scroll position without easing — used on init/resize. */
  Hero.prototype.onScroll = function () {
    if (this.static) return;
    this.target = this.scrollProgress();
    this.rendered = this.target;
    this.render(this.rendered);
  };

  Hero.prototype.render = function (value) {
    // Scroll mode maps 0..1 of scroll onto the slides and reserves the tail for
    // the hand-off. Slideshow mode has no hand-off: the value *is* the index.
    var rel = this.slideshow ? 0 : Math.max(0, (value - this.zone) / (1 - this.zone));
    var bp = this.slideshow ? value : (Math.min(value, this.zone) / this.zone) * this.count;
    var index = this.slideshow
      ? this.normalIndex(value)
      : Math.max(0, Math.min(this.count - 1, Math.floor(bp)));

    var relScale = 1 - 0.22 * rel;
    var relFade = 1 - rel;

    this.beds.forEach(
      function (el) {
        this.place(el, parseInt(el.getAttribute('data-amx-bed'), 10), 70, bp, relFade, relScale, rel);
      }.bind(this)
    );

    this.panels.forEach(
      function (el) {
        this.place(el, parseInt(el.getAttribute('data-amx-tx'), 10), 34, bp, relFade, relScale, rel);
      }.bind(this)
    );

    // Backgrounds cross-fade with a slow push-in only — translating them would
    // drag their own edges into frame. Photographs want a slightly softer
    // dissolve than text, but on the same aligned schedule: this used to run
    // over three slide-widths, so up to three photos were on screen at once and
    // the previous one was always faintly there.
    var bgFade = Math.min(0.55, this.fade * 1.25);
    var bgHold = 1 - bgFade;
    var wrap = this.slideshow && this.count > 1 ? this.count : 0;

    this.bgs.forEach(function (el) {
      var t = bp - parseInt(el.getAttribute('data-amx-bg'), 10);

      // Same wrapping the panels get. Without it, at the loop point every
      // background falls outside its window at once and the frame goes blank.
      if (wrap) {
        var half = wrap / 2;
        while (t > half) t -= wrap;
        while (t < -half) t += wrap;
      }

      var opacity;
      if (t <= -bgFade || t >= 1) opacity = 0;
      else if (t < 0) opacity = (t + bgFade) / bgFade;
      else if (t <= bgHold) opacity = 1;
      else opacity = (1 - t) / bgFade;

      el.style.opacity = String(Math.max(0, Math.min(1, opacity)) * relFade);
      el.style.transform = 'scale(' + (1.05 - 0.05 * Math.max(0, Math.min(1, t + 0.4))) + ')';
    });

    // Scroll mode only. As a slide it carries data-amx-tx and is placed by the
    // panel loop above like any other, so this would fight it.
    if (this.handoff && !this.slideshow) {
      this.handoff.style.opacity = String(Math.max(0, (rel - 0.35) / 0.5));
      this.handoff.style.transform = 'translateY(' + (26 - rel * 26) + 'px)';
    }

    // Scroll mode only — the bar reports position through the scroll track,
    // which has no meaning once arrows drive the slides.
    if (this.progress && !this.slideshow) {
      this.progress.style.width = (value * 100).toFixed(2) + '%';
    }

    this.setActive(rel < 0.5 ? index : -1);
  };

  Hero.prototype.setActive = function (index) {
    if (index === this.active) return;
    this.active = index;
    this.tabs.forEach(function (tab) {
      var own = parseInt(tab.getAttribute('data-amx-tab'), 10);
      tab.setAttribute('aria-pressed', own === index ? 'true' : 'false');
    });
  };

  Hero.prototype.goTo = function (i) {
    if (this.slideshow) {
      // Travel whichever way round is shorter, so picking slide 1 from slide 3
      // moves forward rather than rewinding through 2.
      var here = ((this.target % this.count) + this.count) % this.count;
      var there = ((i % this.count) + this.count) % this.count;
      var delta = there - here;
      var half = this.count / 2;
      if (delta > half) delta -= this.count;
      if (delta < -half) delta += this.count;
      this.step(delta);
      return;
    }

    if (this.static) {
      this.select(i);
      return;
    }
    var m = this.metrics();
    window.scrollTo({
      top: m.top + (m.span * (this.zone * (i + 0.35))) / this.count,
      behavior: 'smooth',
    });
  };

  /* -------------------------------------------------------------- Reveal -- */

  function initReveal(scope) {
    var els = Array.prototype.slice.call((scope || document).querySelectorAll('.amx-reveal'));
    if (!els.length) return;

    if (reduceMotion.matches || !('IntersectionObserver' in window)) {
      els.forEach(function (el) {
        el.classList.add('amx-reveal--in');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('amx-reveal--in');
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    );

    els.forEach(function (el) {
      observer.observe(el);
    });
  }

  /* --------------------------------------------------------------- Video -- */

  function initVideo(scope) {
    var buttons = Array.prototype.slice.call(
      (scope || document).querySelectorAll('[data-amx-play]')
    );
    buttons.forEach(function (button) {
      if (button.dataset.amxBound) return;
      button.dataset.amxBound = '1';
      button.addEventListener('click', function () {
        var media = button.parentElement.querySelector('video, iframe');
        if (!media) return;

        if (media.tagName === 'IFRAME') {
          media.src = media.dataset.amxSrc || media.src;
          media.removeAttribute('hidden');
        } else {
          media.controls = true;
          media.play();
          media.onpause = function () {
            if (media.currentTime === 0) button.style.display = 'flex';
          };
        }
        button.style.display = 'none';
      });
    });
  }

  /* ------------------------------------------------------------ Scroll cue -- */

  /**
   * A badge that trails the cursor over pinned sections, so visitors realise
   * the section is scroll-driven rather than stuck. Pointer-only: on touch
   * there is no cursor to attach to, and the gesture is obvious anyway.
   *
   * Hides once the section is nearly played out — by then the message has
   * landed and it would just be in the way.
   */
  function initCue() {
    var targets = Array.prototype.slice.call(document.querySelectorAll('[data-amx-scrollcue]'));
    if (!targets.length) return;
    if (reduceMotion.matches) return;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    if (document.querySelector('.amx-cue')) return;

    var cue = document.createElement('div');
    cue.className = 'amx-cue';
    cue.setAttribute('aria-hidden', 'true');
    cue.innerHTML =
      '<span class="amx-cue__ring"></span><span class="amx-cue__label"></span>';
    document.body.appendChild(cue);

    var label = cue.querySelector('.amx-cue__label');
    var x = 0;
    var y = 0;
    var tx = 0;
    var ty = 0;
    var active = null;
    var raf = 0;

    function frame() {
      // Trails slightly behind the pointer; a cue pinned exactly to the cursor
      // reads as a UI element rather than a hint.
      x += (tx - x) * 0.16;
      y += (ty - y) * 0.16;
      cue.style.transform = 'translate3d(' + x + 'px,' + y + 'px,0)';

      if (Math.abs(tx - x) > 0.1 || Math.abs(ty - y) > 0.1) {
        raf = window.requestAnimationFrame(frame);
      } else {
        raf = 0;
      }
    }

    function nudge() {
      if (!raf) raf = window.requestAnimationFrame(frame);
    }

    /** True while the section still has meaningful scroll left. */
    function stillRunning(el) {
      var top = 0;
      var node = el;
      while (node) {
        top += node.offsetTop;
        node = node.offsetParent;
      }
      var span = Math.max(1, el.offsetHeight - window.innerHeight);
      var progress = (window.scrollY - top) / span;
      return progress < 0.92;
    }

    /* Anything the visitor can act on. The cue is 92px wide and sits under the
       pointer, so over a link or button it covers the very thing being aimed
       at — and it is telling them to scroll when they are about to click. */
    var INTERACTIVE = 'a, button, input, select, textarea, summary, label, [role="button"], [role="link"], [tabindex]:not([tabindex="-1"])';

    document.addEventListener(
      'mousemove',
      function (e) {
        tx = e.clientX;
        ty = e.clientY;

        var over = null;
        for (var i = 0; i < targets.length; i++) {
          var r = targets[i].getBoundingClientRect();
          if (e.clientY >= r.top && e.clientY <= r.bottom && r.top < window.innerHeight && r.bottom > 0) {
            over = targets[i];
            break;
          }
        }

        // The cue is pointer-events:none, so e.target is genuinely what is
        // under the cursor rather than the cue itself.
        var onControl = e.target && e.target.closest && e.target.closest(INTERACTIVE);

        if (over && !onControl && stillRunning(over)) {
          if (active !== over) {
            active = over;
            label.textContent = over.getAttribute('data-amx-scrollcue') || 'Scroll';
            // Jump straight to the pointer on first show, then trail.
            x = tx;
            y = ty;
          }
          cue.classList.add('amx-cue--visible');
        } else {
          active = null;
          cue.classList.remove('amx-cue--visible');
        }

        nudge();
      },
      { passive: true }
    );

    document.addEventListener('mouseleave', function () {
      active = null;
      cue.classList.remove('amx-cue--visible');
    });

    window.addEventListener(
      'scroll',
      function () {
        if (active && !stillRunning(active)) {
          active = null;
          cue.classList.remove('amx-cue--visible');
        }
      },
      { passive: true }
    );
  }

  /* ------------------------------------------------------------- Parallax -- */

  /**
   * Slow drift on page-hero imagery. The media box is inset beyond the section
   * on both sides, so it can move without ever exposing an edge.
   */
  function initParallax(scope) {
    if (reduceMotion.matches) return;

    var roots = Array.prototype.slice.call(
      (scope || document).querySelectorAll('[data-amx-parallax]')
    );
    if (!roots.length) return;

    var ticking = false;

    function apply() {
      ticking = false;
      roots.forEach(function (root) {
        var img = root.querySelector('.amx-pagehero__media img');
        if (!img) return;

        var rect = root.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > window.innerHeight) return;

        // -1 above the viewport through +1 below it.
        var mid = rect.top + rect.height / 2;
        var progress = (mid - window.innerHeight / 2) / window.innerHeight;
        img.style.transform = 'translate3d(0,' + (progress * 6).toFixed(2) + '%,0)';
      });
    }

    function request() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(apply);
    }

    window.addEventListener('scroll', request, { passive: true });
    window.addEventListener('resize', request, { passive: true });
    apply();
  }

  /* ----------------------------------------------------------------- Boot -- */

  function boot(scope) {
    Array.prototype.slice.call((scope || document).querySelectorAll('[data-amx-hero]')).forEach(
      function (root) {
        if (root.dataset.amxBound) return;
        root.dataset.amxBound = '1';
        new Hero(root);
      }
    );
    initReveal(scope);
    initVideo(scope);
    initParallax(scope);
    initCue();
  }

  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', function () { boot(); });

  /* ------------------------------------------------------------ swatches --
   * The lift, the zoom and the highlight are all CSS. This only tells the CSS
   * where the finger is, by writing the contact point onto the chip. Delegated
   * from the document, so swatches added by the editor need no rebinding.
   */
  (function () {
    var fine = window.matchMedia('(hover: hover) and (pointer: fine)');

    document.addEventListener(
      'pointermove',
      function (e) {
        if (!fine.matches) return;
        var chip = e.target.closest ? e.target.closest('.amx-swatch__chip') : null;
        if (!chip) return;

        var box = chip.getBoundingClientRect();
        if (!box.width) return;
        // Two decimals is finer than any screen can show, and keeps the value
        // written on every move short.
        var px = (((e.clientX - box.left) / box.width) * 100).toFixed(2);
        var py = (((e.clientY - box.top) / box.height) * 100).toFixed(2);
        chip.style.setProperty('--amx-touch-x', px + '%');
        chip.style.setProperty('--amx-touch-y', py + '%');
      },
      { passive: true }
    );

    /* Reset on the way out, so the next approach starts from the middle
       rather than snapping from wherever the pointer happened to leave. */
    document.addEventListener(
      'pointerleave',
      function (e) {
        var chip = e.target.closest ? e.target.closest('.amx-swatch__chip') : null;
        if (!chip) return;
        chip.style.removeProperty('--amx-touch-x');
        chip.style.removeProperty('--amx-touch-y');
      },
      true
    );
  })();

  /* Theme editor re-renders sections in place. */
  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target);
  });
})();
